import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector, useDispatch, batch } from 'react-redux';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import AssetActions from 'components/AssetActions';
import { useScrollIntoView } from 'components/AssetList/hooks';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import LoadingOverlay from 'components/LoadingOverlay';
import { ModalType, ProcessType } from 'const';
import { addSelectedAssetPanelComponent, removeSelectedAssetPanelComponent } from 'containers/App/actions';
import { selectedComponentItem } from 'containers/App/selectors';
import { deleteComponent } from 'containers/Documents/actions';
import { showModal } from 'containers/ModalManager/actions';
import { setAssetIdToScroll as setAssetIdToScrollAction, setSelectedCitation } from 'containers/ProjectPanel/actions';
import * as ProjectPanelModels from 'containers/ProjectPanel/models';
import { selectCitationCount, assetIdToScroll as assetIdToScrollSelector } from 'containers/ProjectPanel/selectors';
import { Priority, useClickOutside } from 'hooks/useClickOutside';
import { ReferenceCitationMap } from 'models';
import { removeTags, trimText } from 'utils/html';
import styles from './styles.module.scss';
import { useCitationItemDragSource } from './useCitationItemDragSource';

type Props = {
  allowDelete: boolean;
  citation: ReferenceCitationMap;
  dataTestId?: string;
};

const CitationItem: React.FC<Props> = (props) => {
  const {
    allowDelete,
    citation,
    dataTestId = 'citationItem',
  } = props;
  const dispatch = useDispatch();
  const citationId = citation.get('id');
  const number = citation.get('number');
  const product = citation.get('product');
  const productName = product?.first();
  const citationCount = useSelector(selectCitationCount(citationId));
  const isSelected = useSelector(selectedComponentItem(citationId));
  const assetIdToScroll = useSelector(assetIdToScrollSelector);
  const setAssetIdToScroll: ProjectPanelModels.ActionCreator.SetAssetIdToScroll = (assetId) => {
    return dispatch(setAssetIdToScrollAction(assetId));
  };
  const { isDragging, dragRef, preview, documentIdsAreBeingUploaded } = useCitationItemDragSource({ component: citation });

  const { elementRef } = useScrollIntoView(citationId, assetIdToScroll, setAssetIdToScroll);
  React.useEffect(() => { preview(getEmptyImage(), { captureDraggingState: true }); }, [preview]);
  const { container } = useClickOutside<HTMLButtonElement>(Priority.SELECTION, {
    active: isSelected,
    onClickOutside: () => dispatch(removeSelectedAssetPanelComponent(citationId)),
  });

  const showCitationDetails = (): void => {
    batch(() => {
      dispatch(setSelectedCitation(citation));
      dispatch(showModal(ModalType.REFERENCE_CITATION_DETAILS));
    });
  };

  const deleteCitation = (): void => {
    const entityType = citation.get('entityType');
    dispatch(deleteComponent(citationId, entityType));
  };

  const onSelectHandler = (): void => {
    dispatch(addSelectedAssetPanelComponent(citationId));
  };

  const citationText = citation.get('text');
  const isCitationBeingUploaded = documentIdsAreBeingUploaded.has(citationId);
  const initialText = removeTags(citationText ?? '');
  const endText = trimText(initialText);

  const handleDragStart = (event: React.DragEvent): void => {
    event.dataTransfer.setData('text/plain', JSON.stringify({ referenceId: citationId }));
  };

  return (
    <AccessibleDiv
      className={classNames(styles.CitationItemWrapper, {
        [styles.dragging]: isDragging,
        [styles.selected]: isSelected && allowDelete,
      })}
      ref={elementRef}
      onMouseDown={onSelectHandler}
      data-testid={dataTestId}
    >
      <LoadingOverlay isLocked={isCitationBeingUploaded} activeProcessType={ProcessType.DOCUMENT_UPLOADING} spinnerSize="md"
        dataTestId={`${dataTestId}-loadingOverlay`} />

      {dragRef(
        <button
          className={classNames(styles.CitationItem, {
            [styles.hide]: isDragging,
          })}
          ref={container}
          onDragStart={handleDragStart}
          data-testid={`${dataTestId}-button`}
        >
          <div className={styles.content} data-testid={`${dataTestId}-content`}>
            <div className={styles.citation} data-testid={`${dataTestId}-content-citation`}>
              <div className={styles.iconWrapper} data-testid={`${dataTestId}-content-citation-icon`}>
                <Icon type={IconType.REFERENCE_CITATION} size="sm-md" color="primary" dataTestId={`${dataTestId}-content-citation`} />
                {_.isFinite(citationCount) && allowDelete && <div className={styles.counter}
                  data-testid={`${dataTestId}-content-citation-icon-wrapper`}>{citationCount}</div>}
              </div>
              <div className={styles.info} data-testid={`${dataTestId}-content-citation-info`}>
                <div className={styles.info__primary} data-testid={`${dataTestId}-content-citation-info-primary`}>
                  <span dangerouslySetInnerHTML={{ __html: initialText }}
                    data-testid={`${dataTestId}-content-citation-info-primary-initialText`}/>
                  <span className={styles.info__primary__truncate} dangerouslySetInnerHTML={{ __html: endText }}
                    data-testid={`${dataTestId}-content-citation-info-primary-endText`}/>
                </div>
              </div>
            </div>
            <div className={styles.name} data-testid={`${dataTestId}-content-name`}>
              <span className={styles.name__primary} data-testid={`${dataTestId}-content-name-number`}>
                {number}
              </span>
              {productName && <span className={styles.name__secondary} data-testid={`${dataTestId}-content-name-productName`}>
                {productName}
              </span>}
            </div>
          </div>
          {
            !isCitationBeingUploaded &&
              <AssetActions
                onDelete={allowDelete ? deleteCitation : undefined}
                onPreviewOpen={showCitationDetails}
                dataTestId={`${dataTestId}-assetActions`}
              />
          }
        </button>,
      )}

    </AccessibleDiv>
  );
};

export default CitationItem;
